import { create } from 'zustand';
import { api } from '../api/client';
import type { Wedding, WeddingMember, WeddingEvent, Task, Guest, GuestGroup, Expense, Vendor, ScheduleRecord, PaginatedResponse, MemberStatus, ModuleName, AccessLevel, Countdown } from '../types';

const LAST_WEDDING_KEY = 'colibri.last_wedding_id';

interface WeddingState {
  weddings: Wedding[];
  currentWedding: Wedding | null;
  members: WeddingMember[];
  events: WeddingEvent[];
  tasks: Task[];
  guests: Guest[];
  guestGroups: GuestGroup[];
  expenses: Expense[];
  vendors: Vendor[];
  schedule: ScheduleRecord[];
  isLoading: boolean;
  weddingsLoaded: boolean;
  error: string | null;

  loadWeddings: () => Promise<void>;
  selectWedding: (id: string) => Promise<void>;
  createWedding: (data: Partial<Wedding>) => Promise<Wedding>;
  updateWedding: (id: string, data: Partial<Wedding>) => Promise<Wedding>;
  deleteWedding: (id: string) => Promise<void>;
  leaveWedding: (id: string) => Promise<void>;
  joinWedding: (inviteCode: string) => Promise<Wedding>;
  loadMembers: () => Promise<void>;
  updateMemberStatus: (memberId: string, status: MemberStatus) => Promise<void>;
  removeMember: (memberId: string) => Promise<void>;
  updateMemberPermissions: (memberId: string, permissions: { module: ModuleName; level: AccessLevel }[]) => Promise<void>;
  regenerateInviteCode: () => Promise<void>;
  transferOwner: (memberId: string) => Promise<void>;
  acceptTransferOwner: (memberId: string) => Promise<void>;
  loadEvents: () => Promise<void>;
  loadTasks: () => Promise<void>;
  countdown: Countdown | null;
  loadCountdown: () => Promise<void>;
  saveCountdown: (data: Partial<Countdown>) => Promise<void>;
  loadGuests: (groupId?: string | null) => Promise<void>;
  createGuest: (data: Partial<Guest>) => Promise<Guest>;
  updateGuest: (id: string, data: Partial<Guest>) => Promise<Guest>;
  deleteGuest: (id: string) => Promise<void>;
  loadGuestGroups: () => Promise<void>;
  createGuestGroup: (name: string) => Promise<GuestGroup>;
  deleteGuestGroup: (id: string) => Promise<void>;
  loadExpenses: () => Promise<void>;
  loadVendors: () => Promise<void>;
  loadSchedule: () => Promise<void>;
  createSchedule: (data: Partial<ScheduleRecord> & { event_id: string; name: string }) => Promise<ScheduleRecord>;
  updateSchedule: (id: string, data: Partial<ScheduleRecord>) => Promise<ScheduleRecord>;
  deleteSchedule: (id: string) => Promise<void>;
  toggleSchedule: (id: string, is_complete: boolean) => Promise<void>;
}

export const useWeddingStore = create<WeddingState>((set, get) => ({
  weddings: [],
  currentWedding: null,
  members: [],
  events: [],
  tasks: [],
  guests: [],
  guestGroups: [],
  expenses: [],
  vendors: [],
  schedule: [],
  isLoading: false,
  weddingsLoaded: false,
  error: null,

  loadWeddings: async () => {
    set({ isLoading: true });
    try {
      const weddings = await api.get<Wedding[]>('weddings');
      set({ weddings, isLoading: false });
      // auto-select (prefer last opened) so cold deep-links have a currentWedding;
      // weddingsLoaded ставим ТОЛЬКО после выбора — иначе гонка с redirect-гейтом
      const { currentWedding } = get();
      if (!currentWedding && weddings.length > 0) {
        const saved = localStorage.getItem(LAST_WEDDING_KEY);
        const target = weddings.find((w) => w.id === saved) || weddings[0];
        await get().selectWedding(target.id);
      }
      set({ weddingsLoaded: true });
    } catch {
      set({ error: 'Ошибка загрузки свадеб', isLoading: false, weddingsLoaded: true });
    }
  },

  selectWedding: async (id: string) => {
    set({ isLoading: true });
    try {
      const wedding = await api.get<Wedding>(`weddings/${id}`);
      set({ currentWedding: wedding, isLoading: false });
      localStorage.setItem(LAST_WEDDING_KEY, id);
    } catch {
      set({ error: 'Ошибка загрузки свадьбы', isLoading: false });
    }
  },

  createWedding: async (data) => {
    set({ isLoading: true });
    try {
      const wedding = await api.post<Wedding>('weddings', data);
      set((s) => ({ weddings: [...s.weddings, wedding], currentWedding: wedding, isLoading: false }));
      localStorage.setItem(LAST_WEDDING_KEY, wedding.id);
      return wedding;
    } catch {
      set({ error: 'Ошибка создания свадьбы', isLoading: false });
      throw new Error('Failed to create wedding');
    }
  },

  updateWedding: async (id, data) => {
    try {
      const wedding = await api.patch<Wedding>(`weddings/${id}`, data);
      set((s) => ({
        weddings: s.weddings.map((w) => (w.id === id ? wedding : w)),
        currentWedding: s.currentWedding?.id === id ? wedding : s.currentWedding,
      }));
      return wedding;
    } catch {
      set({ error: 'Ошибка сохранения свадьбы' });
      throw new Error('Failed to update wedding');
    }
  },

  deleteWedding: async (id) => {
    await api.delete(`weddings/${id}`);
    const { currentWedding, weddings } = get();
    const rest = weddings.filter((w) => w.id !== id);
    set({
      weddings: rest,
      currentWedding: currentWedding?.id === id ? (rest[0] || null) : currentWedding,
    });
    if (currentWedding?.id === id && rest[0]) {
      await get().selectWedding(rest[0].id);
    } else if (currentWedding?.id === id) {
      set({ countdown: null, tasks: [], guests: [], events: [] });
    }
  },

  leaveWedding: async (id) => {
    await api.post(`weddings/${id}/members/leave`);
    const { currentWedding, weddings } = get();
    const rest = weddings.filter((w) => w.id !== id);
    set({
      weddings: rest,
      currentWedding: currentWedding?.id === id ? (rest[0] || null) : currentWedding,
    });
    if (currentWedding?.id === id && rest[0]) {
      await get().selectWedding(rest[0].id);
    } else if (currentWedding?.id === id) {
      set({ countdown: null, tasks: [], guests: [], events: [] });
    }
  },

  joinWedding: async (inviteCode: string) => {
    set({ isLoading: true });
    try {
      const wedding = await api.post<Wedding>('weddings/join', { invite_code: inviteCode });
      set((s) => ({
        weddings: s.weddings.some((w) => w.id === wedding.id) ? s.weddings : [...s.weddings, wedding],
        currentWedding: wedding,
        isLoading: false,
      }));
      localStorage.setItem(LAST_WEDDING_KEY, wedding.id);
      return wedding;
    } catch {
      set({ error: 'Ошибка присоединения', isLoading: false });
      throw new Error('Failed to join wedding');
    }
  },

  loadMembers: async () => {
    const { currentWedding } = get();
    if (!currentWedding) return;
    const members = await api.get<WeddingMember[]>(`weddings/${currentWedding.id}/members`);
    set({ members });
  },

  updateMemberStatus: async (memberId, status) => {
    const { currentWedding, members } = get();
    if (!currentWedding) return;
    const updated = await api.patch<WeddingMember>(`weddings/${currentWedding.id}/members/${memberId}/status`, { status });
    set({ members: members.map((m) => (m.id === memberId ? updated : m)) });
  },

  removeMember: async (memberId) => {
    const { currentWedding, members, weddings } = get();
    if (!currentWedding) return;
    const res = await api.delete<{ detail: string; invite_code?: string }>(
      `weddings/${currentWedding.id}/members/${memberId}`,
    );
    // SEC-REVOKED-02: the backend rotates the invite code on kick
    const next = res?.invite_code ? { ...currentWedding, invite_code: res.invite_code } : currentWedding;
    set({
      members: members.filter((m) => m.id !== memberId),
      currentWedding: next,
      weddings: res?.invite_code ? weddings.map((w) => (w.id === next.id ? next : w)) : weddings,
    });
  },

  updateMemberPermissions: async (memberId, permissions) => {
    const { currentWedding, members } = get();
    if (!currentWedding) return;
    const updated = await api.put<WeddingMember>(`weddings/${currentWedding.id}/members/${memberId}/permissions`, { permissions });
    set({ members: members.map((m) => (m.id === memberId ? updated : m)) });
  },

  regenerateInviteCode: async () => {
    const { currentWedding, weddings, members } = get();
    if (!currentWedding) return;
    await api.post(`weddings/${currentWedding.id}/members/invite-code`);
    const wedding = await api.get<Wedding>(`weddings/${currentWedding.id}`);
    set({
      currentWedding: wedding,
      weddings: weddings.map((w) => (w.id === wedding.id ? wedding : w)),
      members,
    });
  },

  transferOwner: async (memberId) => {
    const { currentWedding } = get();
    if (!currentWedding) return;
    await api.post(`weddings/${currentWedding.id}/members/${memberId}/transfer-owner`);
  },

  acceptTransferOwner: async (memberId) => {
    const { currentWedding, members } = get();
    if (!currentWedding) return;
    const updated = await api.post<WeddingMember>(`weddings/${currentWedding.id}/members/${memberId}/transfer-owner/accept`);
    set({ members: members.map((m) => (m.id === memberId ? updated : m)) });
  },

  loadEvents: async () => {
    const { currentWedding } = get();
    if (!currentWedding) return;
    const events = await api.get<WeddingEvent[]>(`weddings/${currentWedding.id}/events`);
    set({ events });
  },

  countdown: null,

  loadCountdown: async () => {
    const { currentWedding } = get();
    if (!currentWedding) {
      set({ countdown: null });
      return;
    }
    try {
      const cd = await api.get<Countdown>(`weddings/${currentWedding.id}/countdown`);
      set({ countdown: cd });
    } catch (e) {
      console.error('loadCountdown', e);
      set({ countdown: null });
    }
  },

  saveCountdown: async (data) => {
    const { currentWedding } = get();
    if (!currentWedding) throw new Error('Свадьба не выбрана');
    const cd = await api.put<Countdown>(`weddings/${currentWedding.id}/countdown`, data);
    set({ countdown: cd });
  },

  loadTasks: async () => {
    const { currentWedding } = get();
    if (!currentWedding) return;
    const res = await api.get<PaginatedResponse<Task>>(`weddings/${currentWedding.id}/tasks`);
    set({ tasks: res.items });
  },

  loadGuests: async (groupId) => {
    const { currentWedding } = get();
    if (!currentWedding) return;
    const q = groupId ? `?group_id=${groupId}` : '';
    const res = await api.get<PaginatedResponse<Guest>>(`weddings/${currentWedding.id}/guests${q}`);
    set({ guests: res.items });
  },

  loadGuestGroups: async () => {
    const { currentWedding } = get();
    if (!currentWedding) return;
    const groups = await api.get<GuestGroup[]>(`weddings/${currentWedding.id}/guests/groups`);
    set({ guestGroups: groups });
  },

  createGuestGroup: async (name) => {
    const { currentWedding, guestGroups } = get();
    if (!currentWedding) throw new Error('No wedding');
    const group = await api.post<GuestGroup>(`weddings/${currentWedding.id}/guests/groups`, { name });
    set({ guestGroups: [...guestGroups, group] });
    return group;
  },

  deleteGuestGroup: async (id) => {
    const { currentWedding, guestGroups } = get();
    if (!currentWedding) throw new Error('No wedding');
    await api.delete(`weddings/${currentWedding.id}/guests/groups/${id}`);
    set({ guestGroups: guestGroups.filter((g) => g.id !== id) });
    await get().loadGuests();
  },

  createGuest: async (data) => {
    const { currentWedding, guests } = get();
    if (!currentWedding) throw new Error('No wedding');
    const guest = await api.post<Guest>(`weddings/${currentWedding.id}/guests`, data);
    set({ guests: [...guests, guest] });
    return guest;
  },

  updateGuest: async (id, data) => {
    const { currentWedding, guests } = get();
    if (!currentWedding) throw new Error('No wedding');
    const guest = await api.patch<Guest>(`weddings/${currentWedding.id}/guests/${id}`, data);
    set({ guests: guests.map((g) => (g.id === id ? guest : g)) });
    return guest;
  },

  deleteGuest: async (id) => {
    const { currentWedding, guests } = get();
    if (!currentWedding) throw new Error('No wedding');
    await api.delete(`weddings/${currentWedding.id}/guests/${id}`);
    set({ guests: guests.filter((g) => g.id !== id) });
  },

  loadExpenses: async () => {
    const { currentWedding } = get();
    if (!currentWedding) return;
    const res = await api.get<PaginatedResponse<Expense>>(`weddings/${currentWedding.id}/expenses`);
    set({ expenses: res.items });
  },

  loadVendors: async () => {
    const { currentWedding } = get();
    if (!currentWedding) return;
    const vendors = await api.get<Vendor[]>(`weddings/${currentWedding.id}/vendors`);
    set({ vendors });
  },

  loadSchedule: async () => {
    const { currentWedding } = get();
    if (!currentWedding) return;
    const schedule = await api.get<ScheduleRecord[]>(`weddings/${currentWedding.id}/schedule`);
    set({ schedule });
  },

  createSchedule: async (data) => {
    const { currentWedding, schedule } = get();
    if (!currentWedding) throw new Error('No wedding');
    const rec = await api.post<ScheduleRecord>(`weddings/${currentWedding.id}/schedule`, data);
    set({ schedule: [...schedule, rec] });
    return rec;
  },

  updateSchedule: async (id, data) => {
    const { currentWedding, schedule } = get();
    if (!currentWedding) throw new Error('No wedding');
    const rec = await api.patch<ScheduleRecord>(`weddings/${currentWedding.id}/schedule/${id}`, data);
    set({ schedule: schedule.map((r) => (r.id === id ? rec : r)) });
    return rec;
  },

  deleteSchedule: async (id) => {
    const { currentWedding, schedule } = get();
    if (!currentWedding) return;
    await api.delete(`weddings/${currentWedding.id}/schedule/${id}`);
    set({ schedule: schedule.filter((r) => r.id !== id) });
  },

  toggleSchedule: async (id, is_complete) => {
    const { schedule } = get();
    set({ schedule: schedule.map((r) => (r.id === id ? { ...r, is_complete } : r)) });
    try {
      await get().updateSchedule(id, { is_complete });
    } catch {
      set({ schedule: schedule.map((r) => (r.id === id ? { ...r, is_complete: !is_complete } : r)) });
    }
  },
}));
